<div class="bg-white rounded-3xl h-full w-full p-6 flex flex-col gap-4">
  <div class="h-full w-full overflow-auto p-2 flex flex-col gap-4">
    <div class="flex flex-wrap items-end justify-between gap-4">
      <div>
        <p class="text-xl oswald-font font-medium text-gray-500">Maps (beta)</p>
        <p class="text-xs text-gray-500 mt-1">
          Pick a building in the navbar to view its features, or drop a Fulcrum GeoJSON to preview a file.
        </p>
      </div>

      <div class="flex flex-wrap items-end gap-3">
        <div>
          <label for="mapsLevelSelect" class="block mb-2 text-sm font-bold text-gray-900">Level</label>
          <select
            id="mapsLevelSelect"
            [disabled]="!selectedBuilding() || loadingLevels() || loadingFromBuilding()"
            [ngModel]="selectedLevelId()"
            (ngModelChange)="selectedLevelId.set($event)"
            name="mapsLevelSelect"
            class="bg-gray-50 border border-gray-300 text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-56 p-2.5 disabled:opacity-60">
            <option
              *ngIf="selectedLevelId() == null && (!selectedBuilding() || loadingLevels() || levels().length === 0)"
              [ngValue]="null"
              disabled>
              <ng-container *ngIf="!selectedBuilding()">-- Select a building first --</ng-container>
              <ng-container *ngIf="selectedBuilding() && loadingLevels()">-- Loading… --</ng-container>
              <ng-container *ngIf="selectedBuilding() && !loadingLevels() && levels().length === 0">
                -- No levels available --
              </ng-container>
            </option>
            <option *ngFor="let lvl of levels()" [ngValue]="lvl.id">
              {{ lvl.name }} ({{ lvl.id }})
            </option>
          </select>
        </div>

        <div>
          <label for="mapsGeo" class="block mb-2 text-sm font-bold text-gray-900">Or drop a GeoJSON</label>
          <input
            id="mapsGeo"
            type="file"
            accept=".json,.geojson,application/json"
            (change)="onGeoJsonChange($event)"
            class="block text-sm text-gray-900 border border-gray-300 rounded-md bg-gray-50 file:bg-zinc-800 file:text-white file:border-0 file:px-4 file:py-2.5 file:mr-3 cursor-pointer" />
        </div>
      </div>
    </div>

    <div
      *ngIf="!selectedBuilding()"
      class="bg-amber-50 border border-amber-200 rounded-md px-3 py-2.5 text-xs text-amber-800">
      Pick a building from the top navbar to load its features, or drop a GeoJSON below.
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-4 gap-4 flex-1 min-h-0">
      <!-- Map -->
      <div class="lg:col-span-3 relative rounded-md overflow-hidden border border-gray-300 min-h-[60vh]">
        <div #mapEl class="absolute inset-0"></div>
        <!-- File-drop parsing overlay only. Building loads use the full-screen
             SpinnerComponent below so DOM toggling here doesn't reflow the map
             container and trigger spurious `idle` events. -->
        <div
          *ngIf="parsing()"
          class="absolute inset-0 bg-white/60 flex items-center justify-center text-sm text-gray-700 z-[400]">
          Parsing GeoJSON…
        </div>
      </div>

      <!-- Side panel -->
      <div class="flex flex-col gap-3">
        <div>
          <p class="oswald-font text-lg font-medium border-b border-gray-300 pb-1">Source</p>
          <ul class="text-sm space-y-1 mt-2">
            <li>
              <span class="font-bold">Building:</span>
              <span class="ml-1 text-gray-700">{{ selectedBuilding()?.name ?? '—' }}</span>
            </li>
            <li>
              <span class="font-bold">Loaded from:</span>
              <span class="ml-1 text-gray-700">
                <ng-container [ngSwitch]="source()">
                  <span *ngSwitchCase="'building'">API ({{ selectedBuilding()?.name }})</span>
                  <span *ngSwitchCase="'file'">File: {{ fileLabel() }}</span>
                  <span *ngSwitchDefault>—</span>
                </ng-container>
              </span>
            </li>
          </ul>
        </div>

        <div *ngIf="summary() as s">
          <p class="oswald-font text-lg font-medium border-b border-gray-300 pb-1">Summary</p>
          <ul class="text-sm space-y-1 mt-2">
            <li><span class="font-bold">Total features:</span> {{ s.total }}</li>
            <li><span class="font-bold">Rendered:</span> {{ s.rendered }}</li>
            <li *ngIf="s.truncated" class="text-amber-700">
              <span class="font-bold">Truncated</span> at {{ s.maxFeatures }} features. Filter by layer
              to narrow the response.
            </li>
          </ul>

          <div class="mt-3 text-xs text-gray-700">
            <p class="font-bold mb-1">By geometry type</p>
            <div *ngFor="let t of s.byType | keyvalue" class="flex justify-between">
              <span>{{ t.key }}</span>
              <span class="text-gray-500">{{ t.value }}</span>
            </div>
          </div>
        </div>

        <div *ngIf="layers().length > 0">
          <div class="flex items-center justify-between border-b border-gray-300 pb-1">
            <p class="oswald-font text-lg font-medium">Layers</p>
            <div class="flex gap-1 text-xs">
              <button
                type="button"
                (click)="showAllLayers()"
                class="px-2 py-0.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-700">
                All
              </button>
              <button
                type="button"
                (click)="hideAllLayers()"
                class="px-2 py-0.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-700">
                None
              </button>
            </div>
          </div>
          <ul class="text-sm pt-2 max-h-[40vh] overflow-auto pr-1 space-y-0.5">
            <li
              *ngFor="let layer of layers()"
              class="flex items-center gap-2 px-1 py-0.5 rounded hover:bg-gray-50">
              <!-- Checkbox is always enabled — `feature_count` here is
                   per-viewport, not per-building, so a layer with 0 in the
                   current viewport may still be relevant after the user pans. -->
              <input
                type="checkbox"
                [id]="'layer-' + layer.id"
                [checked]="isLayerVisible(layer.id)"
                (change)="toggleLayer(layer.id)"
                class="w-4 h-4 accent-violet-400 cursor-pointer" />
              <label
                [for]="'layer-' + layer.id"
                class="flex-1 text-xs cursor-pointer truncate"
                [title]="layer.name">
                {{ layer.name }}
              </label>
              <span class="text-xs text-gray-500 tabular-nums">{{ layer.feature_count }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</div>
<spinner *ngIf="initialLoading()" pageName="Maps"></spinner>
